import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import SmartCard from '../../shared/components/SmartCard';
import PendencyColumn from './components/PendencyColumn';
import PendencyDetail from './components/PendencyDetail';
import PendencyLinks from './components/PendencyLinks';
import {
  noteByFilterForGrid,
  noteByIds,
  saveEditNote,
  approveNoteByIds,
  disApproveNoteByIds,
} from '../../shared/queries/Appointment';

import GraphQL from '../../GraphQL';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../redux/actions/loadingActions';
import { showNotificationAutoDestroy } from '../../shared/components/ShowNotification';
import {
  ACTIVE_TAB,
  COMMUNICATION_TYPE,
  PENDENCY_DETAILS_TYPE,
  PERIOD_TYPE,
  STATUS_AUDIT,
} from '../../shared/utils/constants';
import { getResponsibleStudentByStudentId } from '../../shared/queries/ResponsibleStudentRelation';
import { relationEmployeeById } from '../../shared/queries/Employee';
import {
  communicationFileListByCommunicationId,
  communicationFindByTypeFilterQuery,
  communicationsByIds,
  approveCommunicationByIds,
  disApproveCommunicationByIds,
} from '../../shared/queries/Communication';
import communicationQuestionRelationFindByCommunicationIdQuery from '../../shared/queries/CommunicationQuestionRelation';
import { relationObjectAuditListByCommunicationId } from '../../shared/queries/RelationObject';
import { useStateWithCallbackLazy } from '../../hooks';
import {
  getFormattedDate,
  updatePendency,
} from '../../shared/utils/smartutils';
import { getAnthropometricMeasurementsById } from '../../shared/queries/WeightMeasure';

const Audit = (props) => {
  const { t, dispatch } = props;
  const isMount = useRef(false);
  const pendencyDetailRef = useRef(null);
  const [selectedAppointmentRecordsIds, setSelectedAppointmentRecordsIds] =
    useState([]);
  const [selectedCommunicationRecordsIds, setSelectedCommunicationRecordsIds] =
    useState([]);
  const [selectedAppointmentRecords, setSelectedAppointmentRecords] = useState(
    []
  );
  const [selectedCommunicationRecords, setSelectedCommunicationRecords] =
    useState([]);
  const [pendencyCommunicationDetails, setPendencyCommunicationDetails] =
    useState([]);
  const [pendencyAppointmentDetails, setPendencyAppointmentDetails] = useState(
    []
  );
  const [lastStudentRecordFilter, setLastStudentRecordFilter] = useState({
    initialDate: new Date(),
    finalDate: new Date(),
    normalSinc: true,
    regularSinc: true,
    badSinc: true,
    status: STATUS_AUDIT.PENDING,
  });
  const [selectedTab, setSelectedTab] = useStateWithCallbackLazy(
    Number(ACTIVE_TAB.ZERO)
  );
  const [pendingRecords, setPendingRecords] = useState({
    appointments: [],
    communications: [],
  });
  const [employee, setEmployee] = useState({});
  const [involved, setInvolved] = useState([]);

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const toggleEditFullScreenForm = () => {
    pendencyDetailRef.current.toggleEditFullScreenForm();
  };

  const getType = () => {
    return selectedTab === Number(ACTIVE_TAB.ZERO)
      ? PENDENCY_DETAILS_TYPE.APPOINTMENT
      : PENDENCY_DETAILS_TYPE.COMMUNICATION;
  };

  const getSelectedAppointments = (appointments) => {
    return appointments.filter((item) => item.selected);
  };

  const getSelectedCommunications = (communications) => {
    return communications.filter((item) => item.selected);
  };

  const getIds = (selectedItems) => {
    return selectedItems.map((item) => item.id);
  };

  const onPendencySelectionHandler = useCallback(
    (records) => {
      if (selectedTab === Number(ACTIVE_TAB.ZERO)) {
        const selectedAppointments = getSelectedAppointments(
          records.appointments
        );
        const selectedIds = getIds(selectedAppointments) || [];
        setSelectedAppointmentRecords(selectedAppointments);
        setSelectedAppointmentRecordsIds(selectedIds);
      }
      if (selectedTab === Number(ACTIVE_TAB.ONE)) {
        const selectedCommunications = getSelectedCommunications(
          records.communications
        );
        const selectedIds = getIds(selectedCommunications);
        setSelectedCommunicationRecords(selectedCommunications);
        setSelectedCommunicationRecordsIds(selectedIds);
      }
    },
    [selectedTab]
  );

  const isSurvey = (communication) => {
    return communication.type === COMMUNICATION_TYPE.SURVEY;
  };

  const loadQuestions = async (communication) => {
    let questionsList = [];
    if (isSurvey(communication)) {
      const query = communicationQuestionRelationFindByCommunicationIdQuery(
        communication.id
      );
      await GraphQL(query, {}).then((res) => {
        const data = [
          ...res.communicationQuestionRelationFindByCommunicationId,
        ];
        questionsList = data.map((item) => ({
          ...item.question,
          type: {
            value: item.question.type,
            label: t(`question.type-description.${item.question.type}`),
          },
        }));
      });
      return questionsList;
    }
    return questionsList;
  };

  const loadFiles = async (id) => {
    const query = communicationFileListByCommunicationId(id);
    let loadedFiles = [];
    await GraphQL(query, {}).then(async (res) => {
      loadedFiles = await res.communicationFileListByCommunicationId.map(
        (file) => ({
          preview: file.fileUrl,
          type: file.fileType,
          fileType: file.fileType,
          fileName: file.fileName,
          status: file.status,
        })
      );
    });
    return loadedFiles;
  };

  const loadMeasures = async (communication) => {
    const query = getAnthropometricMeasurementsById(
      communication.anthropometricMeasurements.id
    );
    let studentMeasures = [];
    await GraphQL(query, {}).then(
      async ({ anthropometricMeasurementListByFilter }) => {
        studentMeasures = {
          ...anthropometricMeasurementListByFilter[0],
          formattedMeasureDate: getFormattedDate(
            anthropometricMeasurementListByFilter[0].measureDate
          ),
        };
      }
    );
    return studentMeasures;
  };

  const hasMeasures = (communication) =>
    communication.type === COMMUNICATION_TYPE.ANTHROPOMETRIC;

  const communicationDetailsMapping = async (communications) => {
    const updatedCommunications = await Promise.all(
      communications.map(async (communication) => ({
        ...communication,
        ...[...selectedCommunicationRecords].find(
          (selectedRecord) => selectedRecord.id === communication.id
        ),
        files: await loadFiles(communication.id),
        questions: await loadQuestions(communication),
        anthropometricMeasurements: hasMeasures(communication)
          ? await loadMeasures(communication)
          : null,
      }))
    );
    return updatedCommunications;
  };

  const appointmentDetailsMapping = (notes) => {
    return notes.map((note) => ({
      ...note,
      ...[...selectedAppointmentRecords].find(
        (selectedRecord) => selectedRecord.id === note.id
      ),
    }));
  };

  const loadAppointmentPendency = (filter) => {
    showLoading();
    const query = noteByFilterForGrid({
      ...filter,
    });
    GraphQL(query, {})
      .then((res) => {
        const updatePendingRecords = { ...pendingRecords };
        setPendingRecords({
          appointments: [...res.noteByFilterForGrid],
          communications: updatePendingRecords.communications,
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadCommunicationPendency = (filter) => {
    const communicationFilter = {
      ...filter,
      initialPeriod: filter.initialDate,
      finalPeriod: filter.finalDate,
      onlySent: false,
      periodType: PERIOD_TYPE.DELIVERY,
      status: filter.status,
    };
    showLoading();
    const query = communicationFindByTypeFilterQuery(communicationFilter);
    GraphQL(query, {})
      .then((res) => {
        const updatePendingRecords = { ...pendingRecords };
        setPendingRecords({
          communications: [...res.communicationListByFilter],
          appointments: updatePendingRecords.appointments,
        });
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadPendencyHandler = (filter) => {
    if (filter) {
      setLastStudentRecordFilter(filter);
    }
    switch (selectedTab) {
      case Number(ACTIVE_TAB.ZERO):
        loadAppointmentPendency(filter);
        setPendencyCommunicationDetails([]);
        break;
      case Number(ACTIVE_TAB.ONE):
        loadCommunicationPendency(filter);
        setPendencyAppointmentDetails([]);
        break;
      default:
    }
  };

  const loadSelectedCommunications = () => {
    showLoading();
    const query = communicationsByIds(selectedCommunicationRecordsIds);
    GraphQL(query, {})
      .then(async (res) => {
        const updateCommunicationDetails = await communicationDetailsMapping(
          res.communicationsByIds
        );
        setPendencyCommunicationDetails(updateCommunicationDetails);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadSelectedAppointment = () => {
    showLoading();
    const query = noteByIds(selectedAppointmentRecordsIds);
    GraphQL(query, {})
      .then((res) => {
        const updatedAppointmentDetails = appointmentDetailsMapping(
          res.noteByIds
        );
        setPendencyAppointmentDetails(updatedAppointmentDetails);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const updateAppointmentItem = (values) => {
    toggleEditFullScreenForm();
    showLoading();
    const mutation = saveEditNote(values);
    GraphQL(mutation, {})
      .then(() => {
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
        setPendencyAppointmentDetails([]);
        loadPendencyHandler(lastStudentRecordFilter);
      })
      .catch(() => {
        showNotificationAutoDestroy(
          'danger',
          t('notifications.error'),
          t('errors.error-to-save')
        );
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadResponsibleByStudentId = (studentId) => {
    showLoading();
    const query = getResponsibleStudentByStudentId(studentId);
    GraphQL(query, {})
      .then((res) => {
        const involvedMapped = res.responsibleStudentRelationListByStudent.map(
          (resp) => ({
            ...resp,
            description: resp.responsible.fullName,
            photoUrl: resp.responsible.profilePhoto,
          })
        );
        setInvolved(involvedMapped);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadRelationsByCommunicationId = (communicationId) => {
    showLoading();
    const query = relationObjectAuditListByCommunicationId(communicationId);
    GraphQL(query, {})
      .then((res) => {
        setInvolved(res.relationObjectListByCommunicationId);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadEmployeeById = (employeeId) => {
    showLoading();
    const query = relationEmployeeById(employeeId);
    GraphQL(query, {})
      .then((res) => {
        setEmployee(res.employeeListByFilter[0]);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const updateStatus = (mutation) => {
    showLoading();
    GraphQL(mutation, {})
      .then(() => {
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
      })
      .finally(() => {
        hideLoading();
        loadPendencyHandler(lastStudentRecordFilter);
        updatePendency(dispatch);
      });
  };

  const onApprove = () => {
    let mutation;
    switch (selectedTab) {
      case Number(ACTIVE_TAB.ZERO):
        mutation = approveNoteByIds(selectedAppointmentRecordsIds);
        break;
      case Number(ACTIVE_TAB.ONE):
        mutation = approveCommunicationByIds(selectedCommunicationRecordsIds);
        break;
      default:
    }
    updateStatus(mutation);
  };

  const onDisapprove = () => {
    let mutation;
    switch (selectedTab) {
      case Number(ACTIVE_TAB.ZERO):
        mutation = disApproveNoteByIds(selectedAppointmentRecordsIds);
        break;
      case Number(ACTIVE_TAB.ONE):
        mutation = disApproveCommunicationByIds(
          selectedCommunicationRecordsIds
        );
        break;
      default:
    }
    updateStatus(mutation);
  };

  const onCloseEditCommunication = () => {
    toggleEditFullScreenForm();
    loadPendencyHandler(lastStudentRecordFilter);
    setSelectedCommunicationRecords([]);
  };

  const onAppointmentRelations = (item) => {
    loadEmployeeById(item.createEmployeeId);
    loadResponsibleByStudentId(item.student.id);
  };

  const onCommunicationRelations = (item) => {
    loadEmployeeById(item.creatorId);
    loadRelationsByCommunicationId(item.id);
  };

  const disposeState = () => {
    setSelectedCommunicationRecords([]);
    setSelectedAppointmentRecords([]);
    setSelectedAppointmentRecordsIds([]);
    setSelectedCommunicationRecords([]);
    setPendencyAppointmentDetails([]);
    setPendencyCommunicationDetails([]);
    setInvolved([]);
    setEmployee({});
  };

  const showDetails = () => {
    return (
      !!pendencyCommunicationDetails.length ||
      !!pendencyAppointmentDetails.length
    );
  };

  const onChangeSelectedTabHandler = (index) => {
    setSelectedTab(index);
    setPendencyCommunicationDetails([]);
    setPendencyAppointmentDetails([]);
  };

  useEffect(() => {
    if (isMount.current) {
      loadPendencyHandler(lastStudentRecordFilter);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (
      selectedAppointmentRecordsIds.length &&
      getType() === PENDENCY_DETAILS_TYPE.APPOINTMENT
    ) {
      loadSelectedAppointment();
    } else {
      setPendencyAppointmentDetails([]);
    }
  }, [selectedAppointmentRecordsIds, selectedTab]);

  useEffect(() => {
    if (
      selectedCommunicationRecordsIds.length &&
      getType() === PENDENCY_DETAILS_TYPE.COMMUNICATION
    ) {
      loadSelectedCommunications();
    } else {
      setPendencyCommunicationDetails([]);
    }
  }, [selectedCommunicationRecordsIds, selectedTab]);

  useEffect(() => {
    setInvolved([]);
    setEmployee({});
  }, [
    selectedTab,
    selectedCommunicationRecordsIds,
    selectedAppointmentRecordsIds,
  ]);

  useEffect(() => {
    isMount.current = true;
    return () => disposeState();
  }, []);

  return (
    <SmartCard title={t('panels.audit')}>
      <div className="audit-wrapper audit-panel">
        <PendencyColumn
          selectedTab={selectedTab}
          onChangeSelectedTab={onChangeSelectedTabHandler}
          onSelectionChange={onPendencySelectionHandler}
          loadPendency={loadPendencyHandler}
          pendingRecords={pendingRecords}
          onChangePendingRecords={setPendingRecords}
          lastStudentRecordFilter={lastStudentRecordFilter}
        />
        {showDetails() && (
          <>
            <PendencyDetail
              t={t}
              type={getType()}
              ref={pendencyDetailRef}
              updateAppointmentItem={updateAppointmentItem}
              onAppointmentRelations={onAppointmentRelations}
              onCommunicationRelations={onCommunicationRelations}
              onCloseEditCommunication={onCloseEditCommunication}
              selectedAppointmentItems={pendencyAppointmentDetails}
              selectedCommunicationItems={pendencyCommunicationDetails}
              onDisapprove={onDisapprove}
              onApprove={onApprove}
            />
            {employee.name && (
              <PendencyLinks involved={involved} employee={employee} />
            )}
          </>
        )}
      </div>
    </SmartCard>
  );
};

Audit.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(memo(Audit))
);
